import { useCookies } from 'react-cookie';
import { useMutation, useQueryClient } from 'react-query';

import { useCartContext } from '@context/cart';
import { useHttpService } from '@hooks/useHttpService';
import { useLogger } from '@hooks/useLogger';
import { useUser } from '@hooks/user/useUser';

import { useCartId, CART_STORAGE_KEY } from './useCartId';
import { CART_QUERY_ID, useCartWithOffer } from './useCartWithOffer';

import { sendAddToCartEvent } from '@services/gtm/cart';
import { CartResponse } from 'types/cart.types';
import { checkIfCartContractTypeSame } from '@helpers/checkIfCartContractTypeSame';
import { useLocale } from '@hooks/useLocale';
import { useContent } from '@hooks/useContent';
import { usePlanData } from 'modules/PDP/hooks/usePlanData';
import { useContentContext } from 'context/content';
import { Plan, PlanContractType } from 'types/plan.types';

export interface CreateCartItemDto {
  planId?: string; //TODO: remove it required after switch to offerItem
  pricingId: string;
  offerItemId?: string; //TODO: Make it required after switch to offerItem
  setId?: string;
  contractType?: PlanContractType;
}

export const useAddToCartWithOfferMutation = () => {
  const { logger } = useLogger({ context: 'useAddToCartMutation' });
  const [, setCookie] = useCookies([CART_STORAGE_KEY]);
  const httpService = useHttpService();
  const queryClient = useQueryClient();
  const cartId = useCartId();
  const { data: user } = useUser();
  const { openCart, setShowItemAdded } = useCartContext();
  const { language, country } = useLocale();
  const { data: plans } = useContent({
    locale: `${country}-${language}`,
    name: `plans-${country}`,
  });
  const { plan } = usePlanData();

  const { data: cart } = useCartWithOffer();
  const { allPlans, allOffers } = useContentContext();

  return useMutation<CartResponse, Error, CreateCartItemDto[]>(
    async addToCartItemsDto => {
      const url = `/cart/${cartId}/add-items`;
      const isMainItem =
        allOffers
          .reduce((acc, offer) => {
            acc.push(...offer.offerItems);
            return acc;
          }, [])
          .find(offerItem => offerItem.id === addToCartItemsDto[0].offerItemId)?.type === 'main';

      logger.info({
        message: 'Adding items to cart',
        params: {
          userId: user?.id,
          cart,
          plan,
          allPlansLength: allPlans.length,
        },
      });

      if (isMainItem && !checkIfCartContractTypeSame(cart, allPlans, plan)) {
        logger.error({
          message: 'Adding items to cart failed',
          params: {
            isMainItem,
            userId: user?.id,
            cart,
            plan,
            allPlansLength: allPlans.length,
          },
        });
        throw new Error('Adding items to cart failed');
      }

      const { data } = await httpService.post(url, addToCartItemsDto);

      return data;
    },
    {
      onSuccess: (cartResponse, addToCartItemsDto = []) => {
        const addedItems = addToCartItemsDto.map(item => {
          const addedFullItem = cartResponse.cart.items.find(
            it => it.offerItem?.id === item.offerItemId && it.pricing.id === item.pricingId
          );

          return addedFullItem || item;
        });

        sendAddToCartEvent(plans, addedItems);

        queryClient.invalidateQueries({ queryKey: CART_QUERY_ID });
        setCookie(CART_STORAGE_KEY, cartResponse.cart.id, { path: '/', sameSite: 'strict', secure: true });
        if ('offerItem' in addedItems[0] && addedItems[0].offerItem && addedItems[0].offerItem.type !== 'additional') {
          setShowItemAdded(true);
          openCart();
        }

        logger.info({
          message: 'Items are added to cart',
          params: {
            userId: user?.id,
            cartId: cartResponse.cart.id,
            addedItems,
            allCartItems: cartResponse.cart?.items,
          },
        });
      },
      onError: (error, addToCartItemsDto) => {
        openCart();

        logger.error({
          message: 'Adding items to cart failed',
          params: {
            error,
            userId: user?.id,
            cartId,
            addToCartItemsDto,
          },
        });
      },
    }
  );
};
